import {users as usersShared} from '@cl/shared';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import accountAnalyticsDataTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/accountAnalyticsDataTool';
import accountAnalyticsTableDataTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/accountAnalyticsTableDataTool';
import accountTopPerformingTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/accountTopPerformingTool';
import accountWorstPerformingTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/accountWorstPerformingTool';
import checkAccountWidgetExistsTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/checkAccountWidgetExistsTool';
import createAccountDashboardTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/createAccountDashboardTool';
import createAccountWidgetTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/createAccountWidgetTool';
import deleteAccountDashboardTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/deleteAccountDashboardTool';
import deleteAccountWidgetTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/deleteAccountWidgetTool';
import getAccountFilterValuesTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/getAccountFilterValuesTool';
import highlightAccountWidgetTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/highlightAccountWidgetTool';
import navigateToAccountDashboardTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/navigateToAccountDashboardTool';
import renameAccountDashboardTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/renameAccountDashboardTool';
import updateAccountWidgetTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/updateAccountWidgetTool';
import persistInlineWidgetToAccountDashboardTool from 'common/src/modules/analytics/copilot/agents/accountAnalyticsAgent/tools/persistInlineWidgetToAccountDashboardTool';
import getBestPracticesTool from 'common/src/modules/analytics/copilot/tools/getBestPracticesTool';
import {doesUserHaveRole} from 'common/src/modules/roles/utils/doesUserHaveRole';
import {RoleTypeName} from 'common/src/modules/users/users.types';
import {SkillName} from 'common/src/modules/shared/copilot/skills/skills.enums';
import skill from 'common/src/modules/shared/copilot/utils/creators/skill';
import {CLStore} from 'common/src/redux/store';

function accountAnalyticsSkill(store: CLStore) {
  const userHasAnalyticsWriteRole = doesUserHaveRole(
    usersShared.userSubject.data,
    RoleTypeName.AnalyticsWrite,
  );

  const dashboardManagementTools = [
    createAccountDashboardTool(),
    renameAccountDashboardTool(store),
    deleteAccountDashboardTool(store),
    deleteAccountWidgetTool(store),
    updateAccountWidgetTool(store),
    createAccountWidgetTool(store),
  ];

  return skill({
    name: SkillName.AccountAnalytics,
    description:
      'Fetches and analyzes account-level analytics data, manages account dashboards and widgets',
    whenToUse:
      'Account-level (cross-campaign) analytics questions, general performance reporting, and campaign-agnostic insights. Handles fetching account-wide analytics data with metrics, dimensions, date ranges, and filters. Can identify top and worst performing elements at the account level, provide best practices, and manage account dashboards and widgets.',
    whenToAvoid:
      'Campaign-specific analytics — those belong to the campaign analytics skill. Analytics about a single campaign.',
    instructions:
      'You are an account-level analytics specialist that fetches and analyzes account-wide performance data, manages account dashboards, and provides insights. You can retrieve analytics data with specific metrics, dimensions, date ranges, and filters at the account level. You can identify top and worst performing elements and provide best practices guidance. When presenting data, show inline widgets when possible and suggest adding useful widgets to dashboards. Only use metrics and dimensions from the available lists in the account context — never invent unsupported values.',
    tools: [
      navigateToAccountDashboardTool(store),
      getBestPracticesTool(),
      accountTopPerformingTool(store),
      accountAnalyticsDataTool(store),
      getAccountFilterValuesTool(store),
      accountAnalyticsTableDataTool(store),
      accountWorstPerformingTool(store),
      highlightAccountWidgetTool(store),
      checkAccountWidgetExistsTool(store),
      persistInlineWidgetToAccountDashboardTool(store),
      ...(userHasAnalyticsWriteRole ? dashboardManagementTools : []),
    ],
    applicableBreadcrumbs: [BreadcrumbName.Dashboard],
  });
}

export default accountAnalyticsSkill;
